import { ApolloClient, ApolloLink } from '@apollo/client'
import { readHeldToken } from 'features/PathwayEditLock'

/**
 * Puts this tab's pathway edit lock signature on every GraphQL request (CE-10579).
 *
 * A header rather than a mutation argument, and a link rather than per-call `context`,
 * for the same reason the server reads it in ContentPolicy rather than in each mutation:
 * a pathway is changed by something like seventy mutations, and a guard whose coverage
 * depends on each call site remembering to opt in is a guard with a hole in it. The
 * holder's own saves are refused if the header is missing, so "most calls send it" is not
 * a state worth having.
 *
 * Deliberately *not* derived from the operation's variables. It was, and that was a bug:
 * the header only reached operations naming `contentParentId`, `contentId` or `id`, and
 * plenty of guarded mutations name a child record instead — `updateNodeComment` takes a
 * comment id, `updateWorkflowNode` a node id, `toggleShowText` neither. The server resolves
 * the pathway through an association and enforces the lock regardless, so those writes were
 * refused for a holder who simply could not produce the signature: lock the pathway for
 * yourself, then find you cannot edit node text, comments or shapes.
 *
 * See readHeldToken for why sending it unconditionally is safe.
 */
export const pathwayLockLink = new ApolloLink((operation, forward) => {
  const token = readHeldToken()

  if (token) {
    operation.setContext(({ headers }) => ({
      headers: { ...headers, 'Pathway-Lock-Token': token },
    }))
  }

  return forward(operation)
})

/**
 * The Apollo client is built and exported by `@curbside-health-inc/client`, so the only
 * way to add a link from here is to rebuild the chain around the one it made. Done once,
 * at boot, before anything has issued a request.
 */
export const installPathwayLockLink = (client: ApolloClient) => {
  const existing = client.link
  if (!existing) return
  client.setLink(ApolloLink.from([pathwayLockLink, existing]))
}

export default installPathwayLockLink
